import SectionOne from '@/features/section-1/application/domain/entities/SectionOne';
import { SectionOneForm } from '@/features/section-1/application/validators/section-one.schema';
import { CitizenshipSelectionOptions } from '@/features/section-1/features/citizenship-selection/application/domain/entities/CitizenshipSelection';
import { useSectionOneDIContext } from '@/features/section-1/infra/di/SectionOneDIContext';
import Section2 from '@/features/section-2/application/domain/entities/Section2';
import { formatDateInput, getRawSSN } from '../utils/utils';
import { workAuthorizationExpiry } from '../utils/work-authorization-expiry';
import CheckMark from './checkMark';
import Letter from './letter';
import Section2Fill from './section2-fill';
import { fillTextStyle } from './fill-styles';

interface Props {
  section1?: SectionOne;
  section2?: Section2;
  className?: string;
}

const x = 152;
const y = 558;
const xDelta = 12;
const ssnBoxTransforms = [
  `matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 ${x} ${y})`,
  `matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 ${x + xDelta} ${y})`,
  `matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 ${x + xDelta * 2} ${y})`,
  `matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 ${x + xDelta * 3} ${y})`,
  `matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 ${x + xDelta * 4} ${y})`,
  `matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 ${x + xDelta * 5} ${y})`,
  `matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 ${x + xDelta * 6} ${y})`,
  `matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 ${x + xDelta * 7} ${y})`,
  `matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 ${x + xDelta * 8} ${y})`,
];
const x1 = 182;
const y1 = 532;
const x2 = 191;
const y2 = 524.5;
const yDelta = -12;

const citizenshipBoxTransforms: Record<
  CitizenshipSelectionOptions,
  {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    stroke: string;
    strokeWidth: number;
    transform: string;
  }
> = {
  [CitizenshipSelectionOptions.CITIZEN]: {
    x1,
    y1,
    x2,
    y2,
    stroke: '#231f20',
    strokeWidth: 1,
    transform: 'matrix(1.33333 0 0 -1.33333 0 1056)',
  },
  [CitizenshipSelectionOptions.NON_CITIZEN]: {
    x1,
    y1: y1 + yDelta,
    x2,
    y2: y2 + yDelta,
    stroke: '#231f20',
    strokeWidth: 1,
    transform: 'matrix(1.33333 0 0 -1.33333 0 1056)',
  },
  [CitizenshipSelectionOptions.PERMANENT_RESIDENT]: {
    x1,
    y1: y1 + yDelta * 2,
    x2,
    y2: y2 + yDelta * 2,
    stroke: '#231f20',
    strokeWidth: 1,
    transform: 'matrix(1.33333 0 0 -1.33333 0 1056)',
  },
  [CitizenshipSelectionOptions.ALIEN]: {
    x1,
    y1: y1 + yDelta * 3,
    x2,
    y2: y2 + yDelta * 3,
    stroke: '#231f20',
    strokeWidth: 1,
    transform: 'matrix(1.33333 0 0 -1.33333 0 1056)',
  },
};

export default function Page1(props: Props) {
  const { section1, section2, ...otherProps } = props;

  // Extract values from section1 prop
  if (!section1?.formFields) {
    return null;
  }

  const section1Data = section1.formFields as SectionOneForm;

  const ssnValues = getRawSSN(section1Data.ssn || '').split('');
  const { statesAdaptor } = useSectionOneDIContext();
  const states = statesAdaptor.getStates();
  const getStateCodeById = (id: string) => {
    const state = states.find((state) => state.id === id);
    return state ? state.code : '';
  };
  const citizenshipStatus = section1Data.citizenshipStatus;

  const renderCitizenshipMark = () => {
    const transform = citizenshipBoxTransforms[citizenshipStatus];
    return <CheckMark transform={transform} />;
  };

  return (
    <svg
      width={816}
      height={1056}
      viewBox="0 0 816 1056"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      xmlns="http://www.w3.org/2000/svg"
      {...otherProps}
    >
      <defs>
        <clipPath clipPathUnits="userSpaceOnUse" id="a">
          <path d="M83.999 774H36v-47.545h47.999z" />
        </clipPath>
      </defs>
      <path
        d="M36 630h540.001v24.001H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h540.501"
        fill="none"
        stroke="#231f20"
        strokeWidth={6}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 406)"
      />
      <path
        d="M0 0v-21.25"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 420.25)"
      />
      <path
        d="M0 0v-21.25"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 420.25)"
      />
      <path
        d="M36 371.998h540.001V402H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 1.1434 0 -53.71)"
      />
      <path
        d="M0 0h540.001"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 35.999)"
      />
      <path
        d="M0 0h540.001"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.72}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 713.999)"
      />
      <path
        d="M0 0h-534.241"
        fill="none"
        stroke="#231f20"
        strokeWidth={5.976}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(573.317 720)"
      />
      <path
        d="M0 0h131.089"
        fill="none"
        stroke="#00f"
        strokeWidth={0.236}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(389.904 410.804)"
      />
      <path
        d="M0 0h154.7"
        fill="none"
        stroke="#00f"
        strokeWidth={0.269}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(278.443 38.349)"
      />
      <g clipPath="url(#a)" transform="matrix(1.33333 0 0 -1.33333 0 1056)">
        <image
          width={1}
          height={1}
          preserveAspectRatio="none"
          transform="matrix(48.012 0 0 47.592 35.994 726.444) matrix(1 0 0 -1 0 1)"
          xlinkHref="data:image/png;base64,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"
          imageRendering="optimizeSpeed"
        />
      </g>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 198.672 758.022)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPS-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="14px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 9.3380003 21 28.784 32.675999 39.675999 46.675999 58.338001 64.554001 72.337997 77 80.5 89.837997 93.730003 97.622002 104.622 108.514 116.298 120.19 124.082 127.974 132.636 139.636 143.136 153.244 159.46001 165.67599 169.56799 174.23 178.12199 184.338 191.338 196 199.892 206.892 214.67599"
          y={0}
        >
          {'Employment Eligibility Verification'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 224.894 741.58)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPS-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="11px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 7.9419999 12.826 18.941999 24.441999 29.326 32.988998 42.152 47.035999 53.152 56.814999 59.564999 65.065002 68.727997 71.477997 80.036003 85.536003 94.698997 99.583 102.641 108.141 114.257 120.373 123.123 129.239 134.123 139.007 145.123 150.007 153.065 156.728 162.228"
          y={0}
        >
          {'Department of Homeland Security'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 211.606 728.38)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPSMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="11px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 7.9419999 10.692 16.808001 22.308001 29.645 32.702999 35.761002 38.819 43.702999 48.587002 54.087002 58.366001 63.866001 66.924004 72.424004 75.174004 80.057999 85.557999 91.057999 93.807999 97.471001 106.029 114.587 117.645 123.145 126.808 131.692 134.75 137.808 143.308 148.808 151.558 157.674 162.558 166.22099 171.72099 174.77901 179.66299 184.547 188.826"
          y={0}
        >
          {'U.S.Citizenship and Immigration Services'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 523.665 761.533)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPS-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="11px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 7.9419999 14.058 22 26.278999 32.395" y={0}>
          {'USCIS'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 518.638 748.333)"
        style={
          {
            //  InkscapeFontSpecification: "'TimesNewRoman Bold'"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="11px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 6.7210002 12.221 17.105 26.268 29.018 33.297001 36.959999" y={0}>
          {'Form I-9'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 506.212 737.395)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRoman"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.776 12.888 18.224001 20.224001 26 30 32 36 40 44 48 50.664001 54.664001 58.664001 62.664001 66.664001"
          y={0}
        >
          {'OMB No.1615-0047 '}
        </tspan>
        <tspan
          x="3.7850001 8.6730003 12.673 16.673 18.896999 21.561001 25.113001 28.225 30.225"
          y={11.952}
        >
          {'Expires 0'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 540.437 725.443)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPSMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0}>
          {'5'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 544.437 725.443)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRoman"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 2.224 6.224 10.224 12.448 16.448 20.448" y={0}>
          {'/31/202'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 568.885 725.443)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPSMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0}>
          {'7'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 36.007 701.686)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 5.3340001 10.22 15.994 21.768 26.653999 28.875 34.648998" y={0}>
          {'START HE'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 75.99 701.686)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 5.7740002 11.108" y={0}>
          {'RE:'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 94.202 701.686)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.3340001 12.444 17.33 19.552 24.438 28.884001 33.330002 36.439999 40.886002 43.106998 50.216999 55.103001 59.549 62.210999 64.431999 68.877998 73.764 78.209999 83.096001 86.206001 90.652 92.873001 95.535004 100.421 104.867 107.088 109.75 114.636 117.746 124.856 127.077 129.299 134.185 138.631 141.293 144.403 149.289 153.735 156.397 158.619 163.505 168.39101 172.83701 175.058 179.504 182.614 187.06 189.28101 193.72701 198.173 202.619 204.841 207.063 211.509 216.395 218.617 223.063 225.284 227.946 232.832 235.05299 239.49899 246.60899 251.495 253.717 258.603 263.04901 267.495 271.94101 276.38699 278.608 284.82999 289.716 294.16199 299.048 301.26901 305.715 310.60101 317.711 322.59698 324.819 329.26501 331.927 334.14899 339.035 343.92099 346.142 348.80399 353.69 355.91199 360.358 362.57901 365.241 370.12701 373.237 380.34698 382.569 387.01401 392.34799 399.45801 404.34399 406.56601 411.452 415.89801 420.34399 423.45401 427.89999 430.121 434.56699 437.677 442.12299 444.34399 446.56601 448.79001 453.23801 458.12601 460.35001 464.798 467.022 469.686 474.57401 477.686"
          y={0}
        >
          {
            'Employers must ensure the form instructions are available to employees when completing this form. Employers are liable for '
          }
        </tspan>
        <tspan
          x="-58.195 -55.530998 -51.083 -48.859001 -46.634998 -44.410999 -39.522999 -34.634998 -32.410999 -29.747 -24.858999 -22.635 -18.187 -13.299 -6.1869998 -1.299 0.92500001 5.3730001 7.5970001 13.821 16.045 18.709 23.597 25.820999 28.485001 33.373001 37.820999 40.044998 43.157001 47.605 52.493 57.381001 59.605 62.716999 67.165001 74.277 78.724998 83.612999 86.277 90.724998 92.948997 95.612999 100.501 103.613 105.837 110.285 115.173 122.285 127.173 129.397 133.845 136.509 138.733 143.621 148.509 150.733 153.397 158.285 160.509 164.957 167.181 169.845 174.733 177.845 184.957 187.181 191.603 196.939 201.38699 205.83501 208.05901 212.94701 217.395 219.619 224.507 230.731 232.955 237.403 242.291 247.179 249.403 252.067 256.95499 261.40302"
          y={9.6000004}
        >
          {'failing to comply with the requirements for completing this form. See below and the'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 357.821 692.086)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 2.224 7.112 11.56 14.224 17.336 22.224001 26.672001 29.336 31.559999 36.448002 41.335999"
          y={0}
        >
          {'Instructions'}
        </tspan>
        <tspan x="45.784 48.007999" y={0}>
          {'.'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 36.007 679.486)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.776 11.552 16.440001 18.664 21.327999 27.104 29.327999 34.664001 40.439999 46.216 48.439999 55.104 57.327999 63.104 68.879997 73.767998 75.991997 82.216003 87.991997 90.216003 95.991997 102.216 107.104 109.328 115.104 120.44 123.104"
          y={0}
        >
          {'ANTI-DISCRIMINATION NOTICE:'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 163.547 679.486)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.336 7.112 8.8879995 11.112 15.56 22.224001 26.672001 28.448 32.896 36.896 41.344002 45.792 49.792 52.015999 56.015999 60.464001 64.912003 67.136002 71.136002 75.584 80.031998 84.480003 88.480003 92.928001 95.152 100.928 105.376 107.152 111.152 115.6 117.824 122.272 126.272 130.272 134.72 139.168 141.392 145.84 150.28799 152.064 156.51199 158.73599 163.18401 167.632 171.632 176.08 182.744 187.192 191.64 193.864 198.312 200.536 202.312 206.75999 211.20799 213.43201 215.65601 220.104 222.328 226.776 229.44 233.888 237.888 242.336 246.784 249.008 251.23199 253.45599 257.90399 260.56799 262.79199 267.67999 272.12799 274.79199 281.45599 283.67999 285.90399 288.56799 293.01599 295.23999 299.71301 305.04901 311.71301 316.16101 317.93701 322.38501 326.38501 330.83301 333.49701 337.49701 339.72101 343.72101 348.16901 352.617 357.065 361.513 363.737 365.961 370.409 374.409 378.409"
          y={0}
        >
          {
            'All employees can choose which acceptable documentation to present for Form I-9. Employers cannot ask '
          }
        </tspan>
        <tspan
          x="-127.54 -123.092 -116.428 -111.98 -110.204 -105.756 -101.756 -97.307999 -92.860001 -88.860001 -86.636002 -84.412003 -79.963997 -77.300003 -75.075996 -70.627998 -66.18 -62.18 -57.731998 -51.068001 -46.619999 -42.172001 -39.948002 -35.5 -33.276001 -31.5 -27.052 -22.604 -20.379999 -18.156 -13.708 -11.484 -7.4840002 -3.036 -0.37200001 1.404 3.628 7.6279998 9.8520002 11.628 16.076 18.299999 22.747999 25.412001 32.076 36.523998 38.748001 40.523998 44.972 49.419998 51.644001 53.419998 57.868"
          y={9.6000004}
        >
          {'employees for documentation to verify information in'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 223.671 669.886)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 5.336 9.7840004 14.232 16.896 19.120001 24.007999 28.896 31.120001" y={0}>
          {'Section 1'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 259.239 669.886)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 2.224 4.448 8.8959999 11.56 13.784 17.784 22.232 26.68 30.68 32.456001 34.68 38.68 40.903999 46.68 51.127998 52.903999 56.903999 61.352001 63.576 68.024002 72.024002 76.024002 80.472 84.919998 87.143997 91.592003 96.040001 97.816002 102.264 104.488 108.936 113.384 117.384 121.832 128.496 132.944 137.392 139.616 144.064 146.28799 148.064 152.51199 156.96001 159.18401 163.632 170.29601 174.744 176.52 180.968 184.968 189.416 193.864 197.864 200.088 206.752 211.2 215.2 217.424 219.64799 224.09599 226.75999 231.20799 235.20799 239.65601 244.104 246.328 248.552 250.776 255.224 257.888"
          y={0}
        >
          {', or specify which acceptable documentation employees must present for'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 519.395 669.886)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 5.336 9.7840004 14.232 16.896 19.120001 24.007999 28.896 31.120001" y={0}>
          {'Section 2'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 557.187 669.886)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.448 7.112" y={0}>
          {'or '}
        </tspan>
        <tspan
          x="-521.17999 -515.84399 -511.396 -506.948 -502.5 -500.724 -496.276 -489.612 -485.164 -480.716 -478.492 -476.26801 -470.93201 -468.70801 -466.48401 -460.70801 -456.26001 -452.26001 -447.81201 -445.14801 -443.37201 -441.14801 -439.37201 -435.37201 -430.92401 -428.70001 -426.92401 -422.47601 -418.02802 -415.80399 -411.35599 -406.90799 -402.45999 -400.23599 -394.45999 -390.01199 -385.564 -383.78799 -381.12399 -376.67599 -374.452 -369.987 -365.099 -362.435 -357.987 -353.539 -351.315 -349.539 -345.091 -340.64301 -338.41901 -333.97101 -327.30701 -322.85901 -321.08301 -316.63501 -312.63501 -308.18701 -303.73901 -299.73901 -297.51501 -293.06699 -291.29099 -289.06699 -286.84299 -282.39499 -279.73099 -275.28299 -270.83499 -268.61099 -266.83499 -262.83499 -260.61099 -256.16299 -251.715 -247.715 -243.267 -238.819 -236.595 -232.147 -227.69901 -225.47501 -223.25101 -218.80299 -214.355 -212.57899 -209.91499 -207.69099 -203.69099 -201.91499 -199.69099 -197.91499 -193.91499 -189.467 -185.019 -181.019 -176.571 -174.795 -170.347 -168.123 -165.899 -164.123 -157.459 -150.795 -149.019 -144.571 -141.907 -137.459 -135.235 -133.459 -129.011 -124.563 -122.339 -118.339 -116.115 -111.667 -109.443 -104.995 -100.995 -98.771004 -96.546997 -92.098999 -89.434998 -87.210999 -82.763 -78.315002 -76.024002 -74.248001 -69.800003 -65.351997 -60.903999 -59.127998 -56.903999 -52.456001 -49.792 -48.015999 -43.568001 -41.792 -37.344002 -35.119999 -28.455999 -24.007999 -20.007999 -17.784 -13.336 -8.8879995 -6.664 -4.888 -3.112 -1.336 3.112 7.5599999 12.008"
          y={9.6000004}
        >
          {
            'Supplement B, Reverification and Rehire. Treating employees differently based on their citizenship, immigration status, or national origin may be illegal'
          }
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 571.014 660.286)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 2.224" y={0}>
          {'.'}
        </tspan>
      </text>
      <path
        d="M36 654.001h540.001V630H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38.16 643.895)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 6.0029998 11.007 16.011 19.007999 21.51 27.009001 32.507999 35.009998 40.014 42.515999 45.018002 51.021 59.021999 64.521004 67.023003 72.522003 77.526001 82.529999 87.533997 90.036003 92.538002 98.037003 101.034 106.533 110.034 118.035 123.039 126.036 128.53799 134.037 139.536 142.03799 147.04201 152.541 158.03999 160.54201 167.03999 170.037 173.034 178.03799 183.04201 186.039 191.043 194.03999 196.54201 202.041 207.53999 210.537"
          y={0}
        >
          {'Section 1. Employee Information and Attestation:'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 251.181 643.895)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 6.0029998 13.5 18.504 20.502001 25.506001 30.006001 35.009998 40.014 44.514 47.015999 54.513 59.516998 64.016998 66.518997 69.021004 73.521004 78.525002 86.022003 91.026001 93.024002 98.028 100.53 105.534 108.036 113.04 118.044 123.048 125.55 130.05 132.048 137.052 142.056 144.558 150.561 155.565 160.065 162.567 164.565 169.569 174.573 177.075 182.07899 184.58099 189.58501 192.08701 194.589 200.088 205.092 208.089 215.586 218.088 220.59 223.58701 228.591 231.093 236.097 241.101 243.603 245.601 250.605 253.10699 258.11099 261.108 263.60999 266.112 271.116 276.12 281.12399 283.62601 286.12799 291.13199"
          y={0}
        >
          {'Employees must complete and sign Section 1 of Form I-9 no later than the'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 549.837 643.895)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 2.997 5.4990001 9 14.004 17.000999" y={0}>
          {'first '}
        </tspan>
        <tspan
          x="-511.677 -506.17801 -501.17401 -496.17001 -493.668 -488.16901 -485.172 -482.67001 -477.66599 -469.66501 -464.16599 -461.664 -456.16501 -451.16101 -443.16 -438.15601 -432.65701"
          y={9}
        >
          {'day of employment'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 120.177 634.895)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 2.5020001 5.0040002 10.008 15.012 17.514 20.016001 25.02 30.024 32.526001 35.028 40.032001 45.035999 47.537998 52.542 55.539001 60.542999 63.044998 68.049004 72.549004 77.049004 82.053001 87.056999 89.558998 91.556999 96.560997 101.565 104.067 109.071 111.573 113.571 118.575 123.579 126.081 131.08501 133.58701 136.089 141.093 144.09 146.592"
          y={0}
        >
          {', but not before accepting a job offer.'}
        </tspan>
      </text>
      <path
        d="M0 0h162.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 604.001)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(198 630.25)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 630.25)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 42 621.973)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 3.892 7.7839999 11.284 13.23 15.176 20.23 24.122 29.952999 33.845001 35.791 38.122002 42.398998 46.291 52.122002 53.675999 55.23 58.73 60.675999 65.730003 69.622002 75.453003 79.345001 81.676003"
          y={0}
        >
          {'Last Name (Family Name)'}
        </tspan>
        <tspan x={0} y={10} style={{ ...fillTextStyle }}>
          {section1Data.lastName}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 42 613)"
      ></text>
      <path
        d="M0 0h144.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(197.75 604.001)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(342 630.25)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 204 621.973)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.277 5.8309999 8.1619997 11.662 13.608 15.554 20.608 24.5 30.330999 34.223 36.168999 38.5 43.945999 45.5 49 52.891998 56.784 58.73 63.784 67.676003 73.507004 77.399002 79.730003"
          y={0}
        >
          {'First Name (Given Name)'}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data.firstName}
        </tspan>
      </text>
      <path
        d="M0 0h72.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(341.751 604.001)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(414.001 630.25)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 348 621.973)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.8309999 7.3850002 11.277 15.169 16.723 20.615 22.561001 24.507 28.399 29.952999 31.899 33.452999 37.345001 38.898998 40.845001 43.175999 44.73 46.675999 48.622002 52.514 56.405998 59.905998"
          y={0}
        >
          {'Middle Initial (if any)'}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data.middleInitial || 'N/A'}
        </tspan>
      </text>
      <path
        d="M0 0h162.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(413.751 604.001)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 630.25)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 420 621.973)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.4460001 7.3920002 11.284 15.176 17.507 19.452999 23.344999 27.237 30.737 32.682999 34.629002 39.682999 43.575001 49.405998 53.298 56.798 58.743999 63.798 67.297997 71.190002 75.082001 77.028 79.359001 80.913002 82.859001 84.805 88.696999 92.588997 96.088997 98.419998"
          y={0}
        >
          {'Other Last Names Used (if any)'}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data.otherName || 'N/A'}
        </tspan>
      </text>
      <path
        d="M0 0h192.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 579.002)"
      />
      <path
        d="M0 0v-25.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(227.999 604.251)"
      />
      <path
        d="M0 0v-25.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 604.251)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 42 595.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 8.5609999 12.453 14.784 18.676001 22.176001 25.676001 27.622 29.952999 34.622002 36.568001 38.898998 42.791 46.682999 48.629002 50.575001 55.629002 59.521 65.351997 69.244003 73.136002 75.467003 77.413002 81.305 85.196999 89.088997 91.035004 96.088997 99.981003 105.812 109.704 112.035"
          y={0}
        >
          {'Address (Street Number and Name)'}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data.address ?? ''}
        </tspan>
      </text>
      <path
        d="M0 0h72.503"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(227.749 579.002)"
      />
      <path
        d="M0 0v-25.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(300.002 604.251)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 234 595.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 8.5609999 10.507 12.453 14.399 19.452999 23.344999 29.176001 33.068001 36.959999 39.291 41.237 43.568001 45.122002 47.068001 49.014 52.905998 56.798 60.298 62.629002"
          y={0}
        >
          {'Apt. Number (if any)'}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data.aptNumber || 'N/A'}
        </tspan>
      </text>
      <path
        d="M0 0h156.502"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(299.75 579.002)"
      />
      <path
        d="M0 0v-25.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(456.002 604.251)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 306 595.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 6.6079998 8.5539999 12.054 14 17.892 20.223 22.169001 26.445999 30.337999 35.391998 39.284"
          y={0}
        >
          {'City or Town'}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data.city ?? ''}
        </tspan>
      </text>
      <path
        d="M0 0h48.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(455.752 579.002)"
      />
      <path
        d="M0 0v-25.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(504.001 604.251)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 462.002 595.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.6690001 6.6149998 10.507 12.453 16.344999" y={0}>
          {'State'}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {getStateCodeById(section1Data.state) ?? ''}
        </tspan>
      </text>
      <path
        d="M0 0h72.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(503.751 579.002)"
      />
      <path
        d="M0 0v-25.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 604.251)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 510.001 595.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.277 6.223 10.892 12.838 17.892 21.784 25.676001 29.568001" y={0}>
          {'ZIP Code'}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data.zipCode ?? ''}
        </tspan>
      </text>
      <path
        d="M0 0h108.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 552.002)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(144 578.251)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 578.251)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 42 569.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 8.9460001 10.892 14.784 16.73 20.622 22.568001 24.514 29.183001 30.737 33.068001 35.014 38.905998 40.852001 43.182999 49.014 54.845001 56.791 60.682999 64.574997 66.521004 70.021004 73.521004 77.021004 80.521004 82.851997"
          y={0}
        >
          {'Date of Birth (mm/dd/yyyy)'}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data?.dob ? formatDateInput(section1Data.dob) : ''}
        </tspan>
      </text>
      <path
        d="M0 0h114.501"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(143.75 552.002)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(258.001 578.251)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(144 578.251)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 150 569.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 7 11.669 13.615 15.561 20.23 24.122 27.622 29.176001 33.068001 34.622002 36.568001 41.237 45.129002 48.629002 52.521 54.852001 56.405998 58.352001 61.852001 63.798 68.851997 72.744003 78.574997 82.467003 86.359001 88.690002"
          y={0}
        >
          {'U.S. Social Security Number'}
        </tspan>
      </text>
      {ssnValues.length > 0 && (
        <>
          {ssnValues.map((digit, idx) => (
            <Letter key={idx} transform={ssnBoxTransforms[idx]} letter={digit} />
          ))}
        </>
      )}
      <path
        d="M0 0v-11.993"
        fill="none"
        stroke="#e2e1e2"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(184.217 565.999)"
      />
      <path
        d="M0 0v-11.993"
        fill="none"
        stroke="#e2e1e2"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(172.47 565.999)"
      />
      <path
        d="M0 0v-11.993"
        fill="none"
        stroke="#e2e1e2"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(160.721 565.999)"
      />
      <path
        d="M0 0v-11.993"
        fill="none"
        stroke="#e2e1e2"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(242.954 565.999)"
      />
      <path
        d="M0 0v-11.993"
        fill="none"
        stroke="#e2e1e2"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(231.207 565.999)"
      />
      <path
        d="M0 0v-11.993"
        fill="none"
        stroke="#e2e1e2"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(219.454 565.999)"
      />
      <path
        d="M0 0v-11.993"
        fill="none"
        stroke="#e2e1e2"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(207.711 565.999)"
      />
      <path
        d="M0 0v-11.993"
        fill="none"
        stroke="#e2e1e2"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(195.961 565.999)"
      />
      <path
        d="M150 565.999h105.024v-11.993H150z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h192.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(257.751 552.002)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(450 578.251)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(258.001 578.251)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 264.002 569.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 10.5 14.392 15.946 19.837999 23.337999 27.23 31.122 32.459 35.959 37.904999 42.574001 48.404999 52.297001 53.851002 55.404999 57.351002 62.02 65.912003 69.804001 72.135002 76.027 79.527 83.027"
          y={0}
        >
          {"Employee's Email Address"}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data.email || 'N/A'}
        </tspan>
      </text>
      <path
        d="M0 0h126.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(449.751 552.002)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 578.251)"
      />
      <path
        d="M0 0v-26.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(450.001 578.251)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 456 569.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 10.5 14.392 15.946 19.837999 23.337999 27.23 31.122 32.459 35.959 37.904999 42.181999 46.074001 47.627998 51.52 55.411999 59.304001 63.195999 67.087997 70.980003 72.926003 77.980003 81.872002 87.703003 91.595001 95.487 97.818001"
          y={0}
        >
          {"Employee's Telephone Number"}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data.phone || 'N/A'}
        </tspan>
      </text>
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 551.999)"
      />
      <path
        d="M0 0v-108.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(173.999 552.249)"
      />
      <path
        d="M0 0v-108.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 552.249)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 42 539.683)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 2.224 4.448 8.8959999 16.007999 18.232 22.68 28.903999 33.352001 36.464001 40.911999 43.136002 45.799999 50.688 55.136002 57.799999 60.023998 62.688 67.136002 72.024002 76.472 79.584 84.031998 86.255997 88.480003 90.704002 95.152 101.376"
          y={0}
        >
          {'I am aware that federal law '}
        </tspan>
        <tspan
          x="0 4.888 8 12.888 17.336 19.559999 24.448 28.896 33.344002 35.568001 38.231998 43.119999 46.231998 48.456001 50.68 57.792 62.68 65.792 68.015999 72.463997 77.351997 82.239998 89.351997 93.800003 98.688004 101.352 103.576 108.024 112.912 117.8 120.024 124.912"
          y={9}
        >
          {'provides for imprisonment and/or'}
        </tspan>
        <tspan
          x="0 2.664 4.888 9.776 14.224 18.672001 20.896 23.559999 28.448 31.559999 33.784 36.448002 40.896 43.119999 47.568001 52.015999 54.240002 58.688 61.352001 65.800003 68.463997 72.912003 80.024002 84.472 89.360001 92.024002 96.472 98.695999 100.92 105.808 108.92 111.144 113.808 118.696 123.144"
          y={18}
        >
          {'fines for false statements, or the '}
        </tspan>
        <tspan
          x="0 4.888 9.3360004 13.784 16.007999 20.896 23.559999 25.784 28.448 32.896 35.119999 39.568001 44.015999 46.240002 51.127998 56.015999 60.464001 65.351997 72.463997 76.912003 81.800003 84.463997 88.912003 91.136002 93.360001 95.584 100.472"
          y={27}
        >
          {'use of false documents, in '}
        </tspan>
        <tspan
          x="0 4.448 9.3360004 14.224 19.112 23.559999 28.007999 30.672001 32.896 37.784 42.672001 44.896 51.119999 53.344002 56.007999 60.896 63.119999 65.783997 70.671997 75.120003 77.344002 81.792 86.68 93.792 98.68 100.904 105.352 108.016 110.24 115.128 120.016 122.24 127.128"
          y={36}
        >
          {'connection with the completion of'}
        </tspan>
        <tspan
          x="0 2.664 7.552 9.776 14.224 16.448 19.112 24 27.112 34.223999 36.448002 40.896 43.119999 45.344002 49.792 52.456001 55.119999 59.568001 64.015999 66.68 68.903999 71.127998 76.015999 80.903999 85.792 90.239998 93.351997 95.575996 100.464 104.912 109.8 114.248 116.472 119.136"
          y={45}
        >
          {'this form. I attest, under penalty'}
        </tspan>
        <tspan
          x="0 4.888 7.552 9.776 14.664 19.112 22.224001 24.448 29.336 32.448002 36.896 39.119999 41.344002 44.007999 48.896 53.344002 56.007999 58.231998 60.896 65.783997 68.008003 72.456001 74.68 76.903999 81.792 84.456001 89.344002 92.456001 99.568001 104.016 106.68 108.904 113.792 118.68"
          y={54}
        >
          {'of perjury, that this information,'}
        </tspan>
        <tspan
          x="0 2.224 7.112 11.56 13.784 18.672001 23.559999 25.784 30.672001 35.560001 37.784 44.896 49.344002 51.568001 56.015999 60.464001 62.688 67.136002 71.584 74.248001 76.472 81.360001 86.248001 88.472 93.360001 96.024002 98.248001 100.912 105.8 110.248 112.472 117.36 122.248"
          y={63}
        >
          {'including my selection of the box'}
        </tspan>
        <tspan
          x="0 4.448 7.112 9.776 14.224 18.672001 21.336 23.559999 28.448 33.335999 35.560001 38.223999 43.112 45.335999 52.448002 56.896 59.119999 63.568001 65.792 68.456001 70.68 74.68 79.127998 84.015999 88.463997 93.351997 95.575996 100.464 102.688 107.576"
          y={72}
        >
          {'attesting to my citizenship or'}
        </tspan>
        <tspan
          x="0 2.224 9.3360004 16.448 18.672001 23.559999 26.672001 31.120001 33.784 36.007999 40.896 45.784 48.007999 52.456001 55.119999 59.568001 62.231998 67.120003 71.568001 73.792 76.015999 78.239998 82.688004 84.912003 87.575996 90.688004 95.575996 100.024 102.248 106.696 111.584 116.472"
          y={81}
        >
          {'immigration status, is true and '}
        </tspan>
        <tspan x="0 4.448 9.3360004 12.448 15.56 20.007999 24.455999 27.120001 29.344" y={90}>
          {'correct.'}
        </tspan>
      </text>
      <path
        d="M0 0h402.502"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(173.749 551.999)"
      />
      <path
        d="M0 0v-18.497"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 552.249)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 180 540.973)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 8.9460001 12.838 16.337999 19.837999 21.784 25.676001 29.568001 33.459999 35.405998 39.298 41.243999 43.189999 45.136002 49.028 52.919998 54.866001 56.812 60.703999 62.257999 63.812 67.704002 72.758003 74.311996 78.204002 82.096001 84.042 87.933998 91.825996 95.325996 99.218002 102.718 104.664 106.61 110.502 112.448 116.34 118.286 120.232 124.124 127.624 129.57001 131.51601 133.46201 137.354 139.3 142.8 146.692 150.584 152.91499 154.86099 158.36099 159.91499 161.86099 163.41499 166.91499 170.80701 174.69901 178.19901 182.091 183.645 187.537 189.483 193.375 195.70599 197.65199 199.20599 205.037 210.868 212.422 216.314 218.645 222.537 224.483 226.037 229.929 233.821 235.767 239.267 241.213 245.105 247.05099 250.94299 254.44299 256.38901 258.72 263.38901 267.28101 271.173 273.11899 277.01099 280.90302 284.79501 288.68701 290.633 294.52499 296.47101 300.36301 304.255 308.147 310.09299 313.98499 315.931 319.823 321.76901 323.715 325.66101 329.55301 333.44501 335.39099 336.94501 340.83701 344.33701 346.28299 348.61401 352.50601 356.00601 357.952 359.50601 363.39801 367.29001 370.79001 372.73599 375.06699 377.013"
          y={0}
        >
          {
            'Check one of the following boxes to attest to your citizenship or immigration status (See page 2 and 3 of the instructions.):'
          }
        </tspan>
      </text>
      <path
        d="M0 0h402.502"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(173.749 521.999)"
      />
      <path
        d="M0 0v-12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 534.248)"
      />
      <path
        d="M0 0v-12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(173.999 534.248)"
      />
      <path
        d="M181.95 532.5h9v-9h-9z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      {renderCitizenshipMark()}
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 197 525.972)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0}>
          {'1. A citizen of the United States'}
        </tspan>
      </text>
      <path
        d="M0 0h402.502"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(173.749 510)"
      />
      <path
        d="M0 0v-12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 522.249)"
      />
      <path
        d="M0 0v-12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(173.999 522.249)"
      />
      <path
        d="M181.95 520.5h9v-9h-9z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 197 513.973)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0}>
          {'2. A noncitizen national of the United States (See Instructions.)'}
        </tspan>
      </text>
      <path
        d="M0 0h216.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(173.749 498.001)"
      />
      <path
        d="M0 0v-12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(389.999 510.25)"
      />
      <path
        d="M0 0v-12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(173.999 510.25)"
      />
      <path
        d="M181.95 508.501h9v-9h-9z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 197 501.974)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0}>
          {'3. A lawful permanent resident (Enter USCIS or A-Number.)'}
        </tspan>
      </text>
      <path
        d="M390 510h186.001v-12.002H390z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 396.001 501.974)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0} style={fillTextStyle}>
          {section1Data.uscisANumberOrAuthorizedAlienNumberForLawfulPermanentResident ?? ''}
        </tspan>
      </text>
      <path
        d="M0 0h186.501"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(389.75 497.998)"
      />
      <path
        d="M181.95 490.501h9v-9h-9z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(0 6)"
      />
      <path
        d="M0 0v-18.497"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 498.248)"
      />
      <path
        d="M0 0v-12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 480.248)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 197 488.866)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0}>
          {'4. An alien authorized to work until'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 331.49 488.866)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 2.3310001 6.223 9.7229996 13.615 15.561 17.507 21.399 25.291 27.237 31.129 33.075001 35.021 36.575001 38.521 40.466999 44.359001 48.250999 51.750999 54.082001"
          y={0}
        >
          {'(exp. date, if any) '}
        </tspan>
        <tspan
          x="-134.254 -132.308 -130.362 -128.416 -124.916 -121.024 -117.132 -115.186 -111.686 -107.794 -103.902 -100.402"
          y={17.5}
        >
          {'If you check'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 389.75 488)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        // writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0} style={fillTextStyle} data-testid="work-authorization-expiry">
          {workAuthorizationExpiry(section1Data)}
        </tspan>
      </text>
      <path
        d="M0 0h60.501"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(389.75 483.001)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 236.534 471.366)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 1.946 4.277 8.1689997 14.392 16.337999 21.392 25.669001 31.892 36.168999 40.061001 42.784 44.73 48.622002"
          y={0}
        >
          {'Item Number 4.'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 287.102 471.366)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 1.946 3.892 7.7839999 11.676 13.622 17.514 19.844999 21.791 25.683001 29.575001 33.466999 35.412998 39.305 41.250999 43.196999 45.143002 49.035 52.926998 56.426998 60.319 62.264999"
          y={0}
        >
          {', enter one of these:'}
        </tspan>
      </text>
      <path
        d="M180 467.999h83.999V456H180z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 193.413 459.973)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 9.7229996 14.777 16.723 21.392 23.337999 28.392 30.723 35.777 40.054001 46.277 50.554001 54.445999 57.168999"
          y={0}
        >
          {'USCIS A-Number'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 193.413 448)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0} style={fillTextStyle}>
          {section1Data.uscisANumberOrAuthorizedAlienNumberForAlienAuthorizedToWork ?? ''}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 265.498 454.262)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="6px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.6680002 9" y={0}>
          {'OR'}
        </tspan>
      </text>
      <path
        d="M276.002 467.999h108V456h-108z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 281.188 459.973)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.277 8.5539999 11.277 17.5 19.445999 21.392 23.723 27.615 31.507 33.452999 38.507 42.784 49.007 50.952999 54.845001 58.737 60.682999 64.959999 69.237 71.182999 76.237 80.514 86.737 91.014 94.905998 97.628998"
          y={0}
        >
          {'Form I-94 Admission Number'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 281.188 448)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0} style={fillTextStyle}>
          {section1Data.authorizedI94Number ?? ''}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 385.5 454.262)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="6px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.6680002 9" y={0}>
          {'OR'}
        </tspan>
      </text>
      <path
        d="M396.001 467.999h180V456h-180z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 401.21 459.973)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.277 8.5539999 11.277 15.169 17.115 21.392 25.669001 27.615 32.284 36.175999 40.068001 43.959999 48.237 52.514 55.237 57.568001 59.514 64.568001 68.845001 75.068001 79.345001 83.237 85.959999 87.905998 91.797997 96.074997 100.352 102.298 107.352 111.629 115.906 120.183 122.514 125.237 129.129 131.075 135.35201 137.683 139.629 141.575 145.467 149.35899 153.636 157.528 161.80499 165.69701 169.589"
          y={0}
        >
          {'Foreign Passport Number and Country of Issuance'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 401.21 448)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0} style={fillTextStyle}>
          {`${section1Data.foreignPassportNumber ?? ''}    ${section1Data.passportCountry ?? ''}`}
        </tspan>
      </text>
      <path
        d="M180 456h83.999v-11.999H180zm96.002 0h108v-11.999h-108zm119.999 0h180v-11.999h-180zM36 443.998h330.002v-24.001H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 42 435.972)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#201c1c"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 6.223 10.115 14.007 17.899 19.844999 23.737 26.068001 29.959999 31.906 35.798 37.743999 39.689999 44.359001 50.189999 54.082001 55.636002 59.528 63.028 66.919998 70.811996"
          y={0}
        >
          {'Signature of Employee'}
        </tspan>
        <tspan
          x={0}
          y={10}
          style={{
            fontFamily: 'Seaweed Script, cursive',
            fontSize: '12px',
          }}
        >
          {section1Data.signature ?? ''}
        </tspan>
      </text>
      <path
        d="M366.002 443.998h209.999v-24.001H366.002z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 372.002 435.972)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#201c1c"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.277 8.1689997 12.061 15.953 19.452999 20.790001 24.290001 26.236 31.290001 35.181999 37.127998 41.02 42.966 45.297001 51.127998 56.959 58.904999 62.797001 66.689003 68.635002 72.135002 75.635002 79.135002 82.635002 84.966003"
          y={0}
        >
          {"Today's Date (mm/dd/yyyy)"}
        </tspan>
        <tspan x={0} y={10} style={fillTextStyle}>
          {section1Data?.signatureDate ? formatDateInput(section1Data.signatureDate) : ''}
        </tspan>
      </text>
      <path
        d="M0 0h540.501"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 420)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 56.191 411.973)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 1.946 4.277 6.223 10.115 12.061 16.337999 19.061001 22.952999 27.23 31.122 33.845001 37.737 40.459999 42.405998 46.298 50.575001 54.852001 56.798 61.075001 63.798 65.744003 68.074997 70.797997 74.690002 78.967003 82.859001 84.805 88.696999 91.028 95.305 98.028 99.973999 103.866 107.758 111.65 113.596 117.488 119.819 123.711 127.988 129.93401 133.826 138.103 142.38 144.326 146.272 150.549 152.495 156.38699 160.664 166.88699 171.164 173.11 177.002 179.33299 181.27901 185.556 189.83299 191.77901 196.448 200.34 204.23199 206.563 208.509 212.786 217.063 219.009 222.901 224.847 226.793 229.12399 233.401 237.293 239.62399 241.57001 245.847 249.739 252.46201 256.354 260.63101 264.90799 266.854 272.685 277.73901 282.40799 286.685 288.63101 292.52301 296.79999 303.02301 307.29999 309.246 313.138 315.46899 319.36099 321.30701 323.638 327.91501 331.80701"
          y={0}
        >
          {
            'If a preparer and/or translator assisted you in completing Section 1, that person MUST complete the '
          }
        </tspan>
        <tspan
          x="333.711 338.38 341.103 344.995 349.272 353.164 355.88699 359.77899 362.50201 364.448 368.34 372.617 376.89401 378.84 383.117 385.84 387.78601 392.06299 394.78601 398.67801 402.95499 406.84698 408.793 412.685 415.01599 419.293 422.01599 423.96201 429.01599 432.90799 435.63101 437.96201 439.90799 442.23901 444.185 448.077 451.96899 454.29999 456.246 460.52301 464.79999"
          y={0}
        >
          {'Preparer and/or Translator Certification '}
        </tspan>
        <tspan
          x="466.746 471.02301 475.29999 477.246 481.91501 485.80701 490.08401 493.97601 495.922 499.814 501.76001"
          y={0}
        >
          {'on Page 3.'}
        </tspan>
      </text>
      <path
        d="M36 402h540.001v-30.002H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 1.15417 0 -58.033)"
      />
      <path
        d="M0 0h144.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(275.752 360.033)"
      />
      <path
        d="M0 0h144.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(275.752 341.996)"
      />
      <path
        d="M0 0v-18.537"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(420.002 360.283)"
      />
      <path
        d="M0 0h156.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(419.752 360.033)"
      />
      <path
        d="M0 0h156.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(419.752 341.996)"
      />
      <path
        d="M0 0v-18.537"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 360.283)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 323.996)"
      />
      <path
        d="M0 0v-18.35"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.999 342.096)"
      />
      <path
        d="M0 0h144.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(275.751 323.996)"
      />
      <path
        d="M0 0v-18.35"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(420.001 342.096)"
      />
      <path
        d="M0 0h156.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(419.751 323.996)"
      />
      <path
        d="M0 0v-18.35"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576 342.096)"
      />
      <path
        d="M36 323.996h90v17.85H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 341.846h90v-17.85H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h144.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(275.752 305.996)"
      />
      <path
        d="M0 0v-18.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(420.002 324.246)"
      />
      <path
        d="M0 0h156.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(419.752 305.996)"
      />
      <path
        d="M0 0v-18.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(420.001 306.246)"
      />
      <path
        d="M0 0h156.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(419.751 287.996)"
      />
      <path
        d="M0 0v-18.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576 306.246)"
      />
      <path
        d="M0 0v-18.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 324.246)"
      />
      <path
        d="M36 305.996h90v18H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h90.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 323.996)"
      />
      <path
        d="M0 0v-18.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(126 324.246)"
      />
      <path
        d="M0 0v-18.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 324.246)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 287.996)"
      />
      <path
        d="M0 0v-18.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.999 306.246)"
      />
      <path
        d="M36 287.996h90v18H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 305.996h90v-18H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h228.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 1.26785 35.75 370.863)"
      />
      <path
        d="M0 0v-12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 372.245)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 1.26785 275.752 370.863)"
      />
      <path
        d="M0 0h144.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 1.26785 431.751 370.863)"
      />
      <path
        d="M0 0v-12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 372.245)"
      />
      <path
        d="M264.03 276.176h311.724v12.05H264.03z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h312.224"
        fill="none"
        stroke="#231f20"
        strokeWidth={1}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.781 288.226)"
      />
      <path
        d="M0 0h312.224"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.781 276.176)"
      />
      <path
        d="M0 0v-12.8"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(575.755 288.726)"
      />
      <path
        d="M0 0v-12.8"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(264.031 288.726)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={1}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 287.993)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 269.996)"
      />
      <path
        d="M0 0v-18.747"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.999 288.493)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 251.996)"
      />
      <path
        d="M0 0v-18.491"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.999 270.237)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 233.996)"
      />
      <path
        d="M0 0v-18.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.999 252.246)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 216.005)"
      />
      <path
        d="M0 0v-18.497"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.999 234.252)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={1}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 216.002)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 198.005)"
      />
      <path
        d="M0 0v-18.747"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.999 216.502)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 180.005)"
      />
      <path
        d="M0 0v-18.491"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.999 198.246)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 162.004)"
      />
      <path
        d="M0 0v-18.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.999 180.254)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 144.007)"
      />
      <path
        d="M0 0v-18.497"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.999 162.254)"
      />
      <path
        d="M264 275.997h311.724V144.002H264z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M264 288.002h11.999v72H264z"
        fill="#c1c0c0"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={1}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(263.749 288.002)"
      />
      <path
        d="M0 0v-72.75"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(275.999 360.252)"
      />
      <path
        d="M0 0v-72.75"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(264 360.252)"
      />
      <path
        d="M36 341.619h90v18.38H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 359.999h90v-18.38H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h138.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(125.75 306.002)"
      />
      <path
        d="M264 360.002h11.999v11.999H264z"
        fill="#c1c0c0"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h12.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 1.26785 263.749 370.871)"
      />
      <path
        d="M36 269.996h90v17.997H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h90.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={1}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 287.993)"
      />
      <path
        d="M0 0h90.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 269.996)"
      />
      <path
        d="M0 0v-18.747"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(126 288.493)"
      />
      <path
        d="M0 0v-18.747"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 288.493)"
      />
      <path
        d="M36 251.996h90v18H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 269.996h90v-18H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 233.996h90v18H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 251.996h90v-18H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 215.999h90v17.997H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 233.996h90v-17.997H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 198.005h90v17.997H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h90.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={1}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 216.002)"
      />
      <path
        d="M0 0h90.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 198.005)"
      />
      <path
        d="M0 0v-18.747"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(126 216.502)"
      />
      <path
        d="M0 0v-18.747"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 216.502)"
      />
      <path
        d="M36 180.005h90v18H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 198.005h90v-18H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 162.005h90v18H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 180.005h90v-18H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 144.008h90v17.997H36z"
        fill="#e2e1e2"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M36 162.005h90v-17.997H36z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0v-33.501"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(456.002 143.248)"
      />
      <path
        d="M0 0v-33.501"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 143.248)"
      />
      <path
        d="M0 0v-34.504"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 144.252)"
      />
      <path
        d="M36 109.997h252V78H36zm252.001 0h198V78h-198zm198 0h90V78h-90z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <path
        d="M0 0h204.501"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(35.75 52)"
      />
      <path
        d="M0 0v-25.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(240.001 77.249)"
      />
      <path
        d="M0 0v-25.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(36 77.249)"
      />
      <path
        d="M0 0h336.5"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(239.751 52)"
      />
      <path
        d="M0 0v-25.499"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056) translate(576.001 77.249)"
      />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38.115 397.6)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 6.0029998 11.007 16.011 19.007999 21.51 27.009001 32.507999 35.009998 40.014 42.515999 45.018002 51.021 59.021999 64.521004 67.023003 72.522003 77.526001 82.529999 86.030998 88.532997 95.030998 100.035 105.039 107.541 112.545 119.547 122.049 127.053 132.552 138.05099 140.55299 146.556 151.56 155.061 157.563 160.56 163.062 168.06599 173.07001 176.067 178.569 184.06799 189.567 192.564"
          y={0}
        >
          {'Section 2. Employer Review and Verification:'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 233.162 397.6)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.336 12 16.448 18.224001 22.672001 26.672001 31.120001 33.784 37.784 40.007999 44.456001 47.119999 49.344002 51.568001 56.015999 60.464001 62.240002 64.903999 67.127998 71.575996 76.024002 78.248001 82.695999 87.143997 89.807999 91.584 95.584 100.032 104.48 106.704 109.368 113.816 118.264 120.928 125.376 129.37601 133.82401 138.272 140.496 144.944 147.168 148.944 152.944 157.392 159.616 166.28 170.728 174.728 176.952 179.17599 183.17599 187.62399 194.28799 198.73599 200.51199 204.96001 207.18401 211.632 213.856 218.304 222.752 227.2 229.424 233.424 235.2 239.64799 244.09599"
          y={0}
        >
          {'Employers or their authorized representative must complete and sign'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 479.529 397.6)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 5.336 9.7840004 14.232 16.896 19.120001 24.007999 28.896 31.120001" y={0}>
          {'Section 2'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 517.321 397.6)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.776 7.552 9.776 14.224 16 20.448 22.672001 24.896 29.344 32.007999 36.456001 40.903999"
          y={0}
        >
          {'within three '}
        </tspan>
        <tspan
          x="-479.20499 -474.75699 -470.30899 -466.30899 -464.53299 -460.08499 -455.63699 -451.63699 -447.63699 -445.41299 -440.965 -436.517 -432.517 -428.517 -426.293 -421.845 -419.621 -417.397 -412.94901 -410.285 -408.061 -405.83701 -401.38901 -396.94101 -394.71701 -390.26901 -383.60501 -379.15701 -377.38101 -372.93301 -368.93301 -364.48499 -360.03699 -358.509 -354.509 -352.285 -350.061 -348.285 -345.621 -341.621 -339.397 -337.173 -332.72501 -328.27701 -324.27701 -322.05301 -317.60501 -315.38101 -313.15701 -308.70901 -302.04501 -297.59698 -295.82101 -291.37299 -287.37299 -280.70901 -276.26099 -271.81299 -269.58899 -267.36499 -265.14099 -260.69299 -256.245 -251.797 -249.573 -242.909 -238.461 -234.461 -232.237 -230.013 -225.565 -221.117 -217.117 -213.117 -211.341 -207.341 -202.89301 -201.117 -199.341 -195.341 -193.117 -188.66901 -184.66901 -180.22099 -173.55701 -171.78101 -167.33299"
          y={7.9829998}
        >
          {
            "business days after the employee's first day of employment, and must physically examine"
          }
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 354.46 389.617)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 2.224 4.448 8.8959999 11.56 13.784 18.232 22.232 26.68 33.344002 35.119999 39.568001 44.015999 46.240002 50.240002 54.688 59.136002 63.136002 64.912003 68.912003 71.136002 75.584 80.031998 82.255997 84.480003 90.255997 92.031998 94.255997 98.704002 100.928 105.376 109.824 112.048 116.496 118.272 120.496 124.944 127.608 132.056 136.504 138.728 140.504 144.504 148.952 151.17599 155.62399 158.28799 162.73599 166.73599 171.18401 175.632 180.08 182.744 187.192"
          y={0}
        >
          {', or examine consistent with an alternative procedure '}
        </tspan>
        <tspan
          x="-316.34399 -311.896 -307.448 -305.224 -300.776 -296.328 -293.664 -291.888 -287.888 -283.44 -278.992 -276.76801 -272.32001 -268.32001 -266.09601 -263.87201 -259.42401 -254.976 -252.752 -247.416 -242.968 -238.968 -236.304 -231.856 -229.632 -225.18401 -222.52 -218.52 -216.29601 -211.84801 -209.62399 -207.39999 -201.62399 -195.84801 -190.51199 -188.28799"
          y={7.7979999}
        >
          {'authorized by the Secretary of DHS,'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 168.401 381.819)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.448 8.8959999 12.896 17.344 24.007999 28.455999 32.903999 35.127998 39.576 41.799999 43.576 48.023998 52.472 54.695999 56.919998 59.584 64.031998 70.695999 72.919998 77.367996 79.143997 83.143997 85.367996 87.592003 92.928001 95.152 101.376 107.152 109.376 113.824 116.048 120.048 124.496 131.16 135.608 137.384 141.832 146.28 148.504 150.28 154.728 159.17599 161.39999 165.84801 168.07201 170.29601 174.744 179.192 183.192 187.64 194.304 198.752 203.2 205.424 209.87199 212.09599 213.87199 218.32001 222.76801 224.992 227.216 229.88 234.328 240.992 243.216 247.664 249.44 253.44 255.664 257.888 263.224 265.448 269.896 274.34399 278.79199 281.01599 285.46399 287.23999 291.23999 293.46399 295.68799 301.46399 303.68799 308.14999 313.48599 317.93399 320.15799 324.60599 327.26999 329.49399 333.94199 338.39001 342.39001 344.61401 349.06201 353.51001 357.95801 359.73401 361.95801 363.73401 368.18201 372.63 377.078 378.854"
          y={0}
        >
          {
            'documentation from List A OR a combination of documentation from List B and List C. Enter any additional '
          }
        </tspan>
        <tspan
          x="-130.285 -125.837 -121.389 -117.389 -112.941 -106.277 -101.829 -97.380997 -95.156998 -90.709 -88.485001 -86.709 -82.261002 -77.813004 -75.588997 -73.813004 -69.364998 -67.140999 -64.917 -60.469002 -56.021 -53.797001 -48.460999 -44.013 -39.564999 -37.789001 -35.564999 -33.789001 -29.341 -24.893 -20.445 -18.669001 -16.445 -14.221 -9.7729998 -7.5489998 -3.1010001 -0.43700001 6.2270002 10.675 12.899 14.675 19.122999 23.570999 25.795 30.243 34.691002 38.691002 40.915001 43.139 47.139 51.587002 56.035 58.258999 60.483002 64.931 68.931 71.154999 73.819 78.266998 82.266998 84.490997 86.266998 90.714996 95.163002 99.163002 101.387"
          y={7.7979999}
        >
          {'documentation in the Additional Information box; see Instructions.'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 183.888 363.681)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.888 7.112 11.56 14.224 16.448 22.224001" y={0}>
          {'List A'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 265.498 364.265)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="6px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.6680002 9" y={0}>
          {'OR'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 333.89 363.68)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.888 7.112 11.56 14.224 16.448 22.224001" y={0}>
          {'List B'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 412.418 363.97)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 5.0539999 10.108 15.162" y={0}>
          {'AND'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 492.89 363.68)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#201c1c"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.888 7.112 11.56 14.224 16.448 22.224001" y={0}>
          {'List C'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 348.782)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 9.3310003 13.223 17.5 23.723 27.615 31.892 34.223 36.168999 40.445999 42.391998 44.723 46.668999 50.561001 52.507 56.398998"
          y={0}
        >
          {'Document Title 1'}
        </tspan>
      </text>
      <path
        d="M126 360.033h137.999v-18.037H126z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      <Section2Fill section1={section1} section2={section2} />
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 330.894)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 1.946 5.4460001 8.9460001 12.838 14.392 18.284 22.176001 24.122 28.791 32.682999 34.629002 38.521 42.412998 44.743999 46.298 48.243999 51.743999"
          y={0}
        >
          {'Issuing Authority'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 312.967)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 8.9460001 12.446 16.337999 22.169001 26.061001 29.952999 31.899 33.845001 38.898998 42.791 48.622002 52.514 56.405998 58.737 60.682999 63.014 64.568001 66.514 68.459999 72.351997 76.244003 79.744003 82.074997"
          y={0}
        >
          {'Document Number (if any)'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 294.97)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 8.1689997 12.061 13.615 15.946 19.837999 21.784 23.337999 27.23 31.122 33.068001 38.122002 42.014 43.959999 47.852001 49.798 52.129002 53.682999 55.629002 57.575001 61.466999 65.359001 68.859001 71.190002"
          y={0}
        >
          {'Expiration Date (if any)'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 276.966)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 9.3310003 13.223 17.5 23.723 27.615 31.892 34.223 36.168999 40.445999 42.391998 44.723 46.668999 50.561001 52.507 56.398998 58.345001 60.675999 62.622002 64.953003 66.899002 70.791 75.068001 78.959999 81.291"
          y={0}
        >
          {'Document Title 2 (if any)'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 267.03 279.885)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#201c1c"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.776 10.664 15.552 17.775999 20.440001 22.664 27.552 32.439999 36.888 39.112 41.335999 43.560001 48.448002 51.112 56 59.112 66.223999 70.671997 73.335999 75.559998 80.447998 85.335999"
          y={0}
        >
          {'Additional Information'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 258.969)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 1.946 5.4460001 8.9460001 12.838 14.392 18.284 22.176001 24.122 28.791 32.682999 34.629002 38.521 42.412998 44.743999 46.298 48.243999 51.743999"
          y={0}
        >
          {'Issuing Authority'}
        </tspan>
      </text>
      {/* VTK-1956: checked only when the Section 2 signer answered that they used the procedure. */}
      <path
        data-testid="dhs-alternative-procedure-box"
        d="M267 156.5h9v-9h-9z"
        fill="none"
        stroke="#231f20"
        strokeWidth={0.5}
        strokeLinecap="square"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        strokeDasharray="none"
        strokeOpacity={1}
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
      {section2?.alternativeProcedureUsed === true && (
        <g
          data-testid="dhs-alternative-procedure-mark"
          stroke="#221e1f"
          strokeWidth={1.5}
          transform="matrix(1.33333 0 0 -1.33333 0 1056)"
        >
          <line x1={268} y1={152} x2={270} y2={149} />
          <line x1={270} y1={149} x2={275} y2={155} />
        </g>
      )}
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 279.36 149.412)"
        style={
          {
            //  InkscapeFontSpecification: "Arial"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#221e1f"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 8.9460001 12.838 16.337999 19.837999 21.784 25.676001 29.568001 31.899 35.791 37.737 39.291 41.237 43.182999 46.682999 50.575001 54.466999 56.412998 60.305 63.805 67.696999 71.588997 73.535004 77.427002 81.319 83.264999 87.156998 88.710999 90.656998 94.549004 96.879997 100.772 104.664 106.61 108.164 111.664 115.556 117.502 121.394 123.725 127.617 131.117 135.009 138.901 142.793 145.12399 149.01601 150.96201 154.854 158.746 160.692 164.584 168.476 170.80701 172.36099 175.86099 179.75301 183.645 185.591 189.483 192.983 194.929 199.983 205.037 209.70599 211.65199 213.59801 217.49001 219.436 223.328 226.828 230.72 236.55099 238.105 241.99699 245.88901 247.83501 251.72701 255.619 259.11899 263.01099 268.84201 272.73401 276.62601 278.57199 282.07199"
          y={0}
        >
          {
            'Check here if you used an alternative procedure authorized by DHS to examine documents.'
          }
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 240.965)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 8.9460001 12.446 16.337999 22.169001 26.061001 29.952999 31.899 33.845001 38.898998 42.791 48.622002 52.514 56.405998 58.737 60.682999 63.014 64.568001 66.514 68.459999 72.351997 76.244003 79.744003 82.074997"
          y={0}
        >
          {'Document Number (if any)'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 222.968)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 8.1689997 12.061 13.615 15.946 19.837999 21.784 23.337999 27.23 31.122 33.068001 38.122002 42.014 43.959999 47.852001 49.798 52.129002 53.682999 55.629002 57.575001 61.466999 65.359001 68.859001 71.190002"
          y={0}
        >
          {'Expiration Date (if any)'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 204.97)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 9.3310003 13.223 17.5 23.723 27.615 31.892 34.223 36.168999 40.445999 42.391998 44.723 46.668999 50.561001 52.507 56.398998 58.345001 60.675999 62.622002 64.953003 66.899002 70.791 75.068001 78.959999 81.291"
          y={0}
        >
          {'Document Title 3 (if any)'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 186.974)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 1.946 5.4460001 8.9460001 12.838 14.392 18.284 22.176001 24.122 28.791 32.682999 34.629002 38.521 42.412998 44.743999 46.298 48.243999 51.743999"
          y={0}
        >
          {'Issuing Authority'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 168.977)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 8.9460001 12.446 16.337999 22.169001 26.061001 29.952999 31.899 33.845001 38.898998 42.791 48.622002 52.514 56.405998 58.737 60.682999 63.014 64.568001 66.514 68.459999 72.351997 76.244003 79.744003 82.074997"
          y={0}
        >
          {'Document Number (if any)'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 150.98)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 8.1689997 12.061 13.615 15.946 19.837999 21.784 23.337999 27.23 31.122 33.068001 38.122002 42.014 43.959999 47.852001 49.798 52.129002 53.682999 55.629002 57.575001 61.466999 65.359001 68.859001 71.190002"
          y={0}
        >
          {'Expiration Date (if any)'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 133.473)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0539999 8.9460001 11.669 14 15.946 18.277 20.223 24.115 28.007 30.337999 32.284 36.561001 40.838001 43.168999 47.061001 49.007 50.952999 54.845001 57.175999 59.507 63.398998 67.291 69.622002 71.568001 73.514 77.791 82.068001 86.345001 90.237 92.959999 94.905998 99.182999 103.075 107.352 111.244 113.19 115.521 119.413 121.359 125.636 127.967 129.91299 134.19 138.082 140.80499 142.75101 147.028 149.75101 153.64301 155.589 157.535 159.866 164.14301 168.035 170.366 172.312 174.64301 178.535 180.866 182.812 184.758 186.70399 190.981 194.873 198.765 202.657 204.603 208.495 212.38699 216.27901 222.502 224.448 228.72501 232.617 236.894 238.84 241.17101 245.448 249.34 251.286 255.563 259.84 263.73199 268.009 274.23199 278.12399 282.401 284.73199 288.62399 290.95499 292.901 297.17801 301.45499 303.401 307.67801 310.401 314.293 318.185 322.077 326.354 328.685 332.577 336.854 338.79999 343.077 346.96899 348.91501 351.246 355.52301 359.41501 361.36099 365.25299 369.53 373.80701 377.69901 381.591 383.922 388.19901 392.091 398.314 402.20599 406.483"
          y={0}
        >
          {
            'Certification: I attest, under penalty of perjury, that (1) I have examined the documentation presented by the above-named '
          }
        </tspan>
        <tspan
          x="0 3.892 10.115 14.392 16.337999 20.615 24.507 28.399 32.291 34.237 36.182999 38.514 42.405998 44.737 46.682999 49.014 53.291 57.182999 59.129002 63.021 67.297997 71.574997 75.467003 79.359001 81.690002 83.636002 85.582001 89.473999 91.805 95.696999 99.973999 101.92 106.197 110.474 114.366 118.643 124.866 128.758 133.035 135.366 139.258 141.589 143.535 147.812 152.089 154.035 157.927 162.20399 166.481 170.373 174.265 176.98801 180.88 182.826 185.157 189.43401 191.38 195.657 199.549 201.495 205.772 209.664 213.94099 218.218 220.164 224.44099 228.33299 230.27901 234.17101 238.448 242.72501 244.67101 247.002 251.27901 253.22501 255.948 259.84 261.78601 265.67801 268.009 271.901 273.84698 276.17801 280.45499 282.401 284.73199 289.009 292.901 294.84698 298.73901 304.96201 309.23901 311.185 315.46201 319.354 323.246 327.138 329.08401 333.36099 337.25299 343.47601 347.36801 351.64499 353.591 355.53699 359.42899 363.70599 367.983 369.92899 372.26001 376.15201 378.483 380.42899 382.76001 387.03699 388.983 391.314 395.591 399.483"
          y={8.3999996}
        >
          {
            'employee, (2) the above-listed documentation appears to be genuine and to relate to the employee named, and (3) to the '
          }
        </tspan>
        <tspan
          x="0 4.277 8.1689997 12.061 14.392 16.337999 20.615 22.945999 24.892 31.115 35.007 36.952999 40.845001 45.122002 49.398998 54.845001 56.791 60.682999 64.959999 69.237 73.128998 75.074997 77.021004 79.351997 83.628998 87.521004 89.467003 93.359001 99.582001 103.859 105.805 110.082 113.974 117.866 121.758 123.704 125.65 129.54201 131.48801 135.38 139.657 141.98801 146.265 150.54201 153.265 155.211 158.711 162.603 166.88 168.826 171.157 175.43401 177.38 182.826 187.103 189.826 193.718 195.664 197.61 201.88699 203.83299 206.164 210.44099 214.33299 216.27901 221.33299 225.61 227.556 229.88699 233.77901 238.056 240.002 244.67101 247.002 250.894 253.22501 257.117 261.009 262.95499"
          y={16.799999}
        >
          {'best of my knowledge, the employee is authorized to work in the United States.'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 462.004 137.176)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.277 5.8309999 8.1619997 11.662 13.608 15.554 20.608 24.5 28 29.945999 33.838001 35.784 37.73 42.398998 48.23 52.122002 53.675999 57.568001 61.068001 66.899002 70.791 74.682999 76.628998"
          y={0}
        >
          {'First Day of Employment '}
        </tspan>
        <tspan
          x="0 2.3310001 8.1619997 13.993 15.939 19.830999 23.723 25.669001 29.169001 32.668999 36.168999 39.668999 42 43.945999"
          y={8.3999996}
        >
          {'(mm/dd/yyyy):'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 37.923 101.973)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 3.892 7.7839999 11.284 13.23 15.176 20.23 24.122 29.952999 33.845001 35.791 37.737 42.014 43.568001 45.898998 49.398998 51.345001 53.291 58.345001 62.237 68.068001 71.959999 73.905998 77.797997 81.690002 85.582001 87.528 91.805 93.359001 95.305 96.859001 100.751 102.697 106.589 108.535 110.481 115.15 120.981 124.873 126.427 130.319 133.819 137.711 140.04201 141.98801 145.88 148.211 150.157 154.826 158.718 160.664 164.556 168.448 170.77901 172.33299 175.83299 179.72501 183.617 185.563 190.617 194.509 198.401 200.73199 204.62399 208.12399 212.01601 215.908 217.854 221.746 223.692 225.246 228.746 232.638"
          y={0}
        >
          {'Last Name, First Name and Title of Employer or Authorized Representative'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 294.004 101.973)"
        style={
          {
            //  InkscapeFontSpecification: "Arial"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 6.223 10.115 14.007 17.899 19.844999 23.737 26.068001 29.959999 31.906 35.798 37.743999 39.689999 44.359001 50.189999 54.082001 55.636002 59.528 63.028 66.919998 69.250999 71.196999 75.088997 77.419998 79.365997 84.035004 87.927002 89.873001 93.764999 97.656998 99.987999 101.542 105.042 108.934 112.826 114.772 119.826 123.718 127.61 129.94099 133.83299 137.33299 141.22501 145.117 147.063 150.955 152.901 154.455 157.955 161.847"
          y={0}
        >
          {'Signature of Employer or Authorized Representative'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 488.933 101.973)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.277 8.1689997 12.061 15.953 19.452999 20.790001 24.290001 26.236 31.290001 35.181999 37.127998 41.02 42.966 45.297001 51.127998 56.959 58.904999 62.797001 66.689003 68.635002 72.135002 75.635002 79.135002 82.635002 84.966003"
          y={0}
        >
          {"Today's Date (mm/dd/yyyy)"}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 38 68.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 10.5 14.392 15.946 19.837999 23.337999 27.23 29.561001 30.898001 34.397999 36.344002 41.013 44.904999 48.404999 49.959 53.851002 57.743 61.243 64.742996 66.689003 70.581001 72.912003 74.858002 80.304001 82.635002 86.527 90.418999 94.310997 95.864998 99.364998 103.257 105.203 106.757 110.649 114.541 116.487 121.541 125.433 131.26401 135.15601"
          y={0}
        >
          {"Employer's Business or Organization Name"}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 245.998 68.975)"
        style={
          {
            //  InkscapeFontSpecification: "ArialMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="7px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.6690001 10.5 14.392 15.946 19.837999 23.337999 27.23 29.561001 30.898001 34.397999 36.344002 41.013 44.904999 48.404999 49.959 53.851002 57.743 61.243 64.742996 66.689003 70.581001 72.912003 74.858002 80.304001 82.635002 86.527 90.418999 94.310997 95.864998 99.364998 103.257 105.203 106.757 110.649 114.541 116.487 121.156 125.048 128.94 131.271 135.16299 138.66299 142.16299 144.10899 146.05499 151.10899 152.66299 154.60899 158.10899 160.05499 163.94701 166.278 168.224 172.50101 176.39301 181.44701 185.339 187.285 189.231 193.89999 195.84599 199.73801 201.68401 205.576 207.522 209.468 213.745 215.69099 220.36 222.306 227.36 231.252 235.144 239.036"
          y={0}
        >
          {"Employer's Business or Organization Address, City or Town, State, ZIP Code"}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 136.623 39.685)"
        style={
          {
            //  InkscapeFontSpecification: "Arial-BoldMT"
          }
        }
        fontVariant="normal"
        fontWeight={700}
        fontStretch="normal"
        fontSize="8px"
        fontFamily="Arial"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 4.888 9.776 12.888 15.112 18.224001 22.672001 27.120001 31.568001 34.68 36.903999 39.568001 41.792 46.240002 50.688 53.352001 55.576 60.464001 65.351997 67.575996 72.463997 75.575996 77.800003 80.912003 85.360001 90.248001 92.472 95.584 100.032 102.256 104.48 108.928 113.816 120.928 125.816 128.03999 132.48801 135.15199 139.60001"
          y={0}
        >
          {'For reverification or rehire, complete '}
        </tspan>
        <tspan
          x="141.82401 147.16 152.048 156.936 161.82401 164.048 168.496 175.608 180.056 184.944 187.608 189.832 195.608 197.832 200.056 205.832 210.28 214.728 219.17599 222.28799 224.51199 227.17599 229.39999 233.84801 238.29601 240.96001 243.18401 248.07201 252.96001 255.18401 259.63199 264.51999 269.40799 271.63199 277.40799 281.85599 286.74399 288.96799 292.07999 296.52802"
          y={0}
        >
          {'Supplement B, Reverification and Rehire '}
        </tspan>
        <tspan
          x="298.73599 303.62399 308.51199 310.73599 316.07199 320.51999 325.40799 329.85599 332.07999 336.52802 338.75201"
          y={0}
        >
          {'on Page 4.'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 36 24.441)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPSMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan
          x="0 5.0040002 9.5039997 12.501 19.503 21.753 24.75 27.747 32.247002 38.997002 44.495998 48.995998 51.498001 54 56.501999 61.001999 65.501999"
          y={0}
        >
          {'Form I-9 Edition'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 108.24 24.441)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPSMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.5" y={0}>
          {'01'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 117.24 24.441)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPSMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0}>
          {'/'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 119.742 24.441)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPSMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 4.5" y={0}>
          {'20'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 128.742 24.441)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPSMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 2.5020001" y={0}>
          {'/2'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 135.744 24.441)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPSMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x={0} y={0}>
          {'5'}
        </tspan>
      </text>
      <text
        xmlSpace="preserve"
        transform="matrix(1.33333 0 0 -1.33333 0 1056) matrix(1 0 0 -1 535.257 24.441)"
        style={
          {
            //  InkscapeFontSpecification: "TimesNewRomanPSMT"
          }
        }
        fontVariant="normal"
        fontWeight={400}
        fontStretch="normal"
        fontSize="9px"
        fontFamily="Times New Roman"
        writingMode="lr-tb"
        fill="#231f20"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
      >
        <tspan x="0 5.0040002 9 13.5 17.496 19.746 24.246 26.496 30.996 33.993 36.243 40.743" y={0}>
          {'Page 1 of 4'}
        </tspan>
      </text>
      <path
        d="M357.821 690.55h45.781v.4h-45.781z"
        fill="#00f"
        fillOpacity={1}
        fillRule="nonzero"
        stroke="none"
        transform="matrix(1.33333 0 0 -1.33333 0 1056)"
      />
    </svg>
  );
}
